import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { isGridArrowKey, moveGridSelection } from '../../shared/grid/navigation';
import type { GameComponentProps } from '../core/types';
import {
  chordInto,
  correctFlagCount,
  isMinesweeperWon,
  minesweeperFailureDelta,
  revealInto,
  type MinesweeperMark,
} from './logic';
import { isMinesweeperPuzzle, MINESWEEPER_CELL_COUNT, type MinesweeperPuzzle } from './types';

type MinesweeperMode = 'reveal' | 'flag';
type MinesweeperStatus = 'playing' | 'won' | 'lost';

type MinesweeperSavedState = {
  version: 1;
  marks: MinesweeperMark[];
  selectedIndex: number;
  mode: MinesweeperMode;
  timerStartedAt: number | null;
  elapsedMs: number;
};

function readSavedState(value: unknown, puzzle: MinesweeperPuzzle): MinesweeperSavedState {
  const fallback: MinesweeperSavedState = {
    version: 1,
    marks: Array.from({ length: MINESWEEPER_CELL_COUNT }, () => 0),
    selectedIndex: puzzle.startIndex,
    mode: 'reveal',
    timerStartedAt: null,
    elapsedMs: 0,
  };
  if (!value || typeof value !== 'object') return fallback;
  const candidate = value as Partial<MinesweeperSavedState>;
  return {
    version: 1,
    marks:
      Array.isArray(candidate.marks) && candidate.marks.length === MINESWEEPER_CELL_COUNT
        ? candidate.marks.map((mark) => (mark === 1 || mark === 2 ? mark : 0))
        : fallback.marks,
    selectedIndex:
      Number.isInteger(candidate.selectedIndex) &&
      candidate.selectedIndex! >= 0 &&
      candidate.selectedIndex! < MINESWEEPER_CELL_COUNT
        ? candidate.selectedIndex!
        : fallback.selectedIndex,
    mode: candidate.mode === 'flag' ? 'flag' : 'reveal',
    timerStartedAt:
      typeof candidate.timerStartedAt === 'number' && Number.isFinite(candidate.timerStartedAt)
        ? candidate.timerStartedAt
        : null,
    elapsedMs:
      typeof candidate.elapsedMs === 'number' && candidate.elapsedMs >= 0 ? candidate.elapsedMs : 0,
  };
}

function formatCounter(value: number): string {
  return Math.max(-99, Math.min(999, value)).toString().padStart(3, '0');
}

const DISPLAY_SEGMENTS = ['a', 'b', 'c', 'd', 'e', 'f', 'g'] as const;

const CELL_NUMBER_BITMAPS: Record<number, readonly string[]> = {
  1: [
    '0001100',
    '0011100',
    '0111100',
    '1111100',
    '0011100',
    '0011100',
    '0011100',
    '0011100',
    '1111111',
    '1111111',
  ],
  2: [
    '0111111110',
    '1111111111',
    '1110000111',
    '0000000111',
    '0000011110',
    '0001111100',
    '0111110000',
    '1111000000',
    '1111111111',
    '1111111111',
  ],
  3: [
    '1111111110',
    '1111111111',
    '0000000111',
    '0000000111',
    '0001111110',
    '0001111110',
    '0000000111',
    '0000000111',
    '1111111111',
    '1111111110',
  ],
  4: [
    '0011101110',
    '0011101110',
    '0111001110',
    '0111001110',
    '1111111111',
    '1111111111',
    '0000001110',
    '0000001110',
    '0000001110',
    '0000001110',
  ],
  5: [
    '1111111111',
    '1111111111',
    '1110000000',
    '1110000000',
    '1111111110',
    '1111111111',
    '0000000111',
    '0000000111',
    '1111111111',
    '1111111110',
  ],
  6: [
    '0111111110',
    '1111111110',
    '1110000000',
    '1110000000',
    '1111111110',
    '1111111111',
    '1110000111',
    '1110000111',
    '1111111111',
    '0111111110',
  ],
  7: [
    '1111111111',
    '1111111111',
    '0000000111',
    '0000000111',
    '0000001110',
    '0000001110',
    '0000011100',
    '0000011100',
    '0000111000',
    '0000111000',
  ],
  8: [
    '0111111110',
    '1111111111',
    '1110000111',
    '1110000111',
    '0111111110',
    '0111111110',
    '1110000111',
    '1110000111',
    '1111111111',
    '0111111110',
  ],
};

const FLAG_BITMAP = [
  '000RR00',
  '0RRRR00',
  'RRRRR00',
  '0RRRR00',
  '000RR00',
  '0000B00',
  '0000B00',
  '00BBBB0',
  'BBBBBBB',
  'BBBBBBB',
] as const;

type MinesweeperFaceState = 'ready' | 'pressed' | 'won' | 'lost';

const FACE_BITMAPS: Record<MinesweeperFaceState, readonly string[]> = {
  ready: [
    '000001111100000',
    '000112222211000',
    '001222222222100',
    '012222222222210',
    '122211222112221',
    '122211222112221',
    '122222222222221',
    '122222222222221',
    '122222222222221',
    '122212222212221',
    '122221222122221',
    '012222111222210',
    '001222222222100',
    '000112222211000',
    '000001111100000',
  ],
  pressed: [
    '000001111100000',
    '000112222211000',
    '001222222222100',
    '012222222222210',
    '122211222112221',
    '122211222112221',
    '122222222222221',
    '122222222222221',
    '122222111222221',
    '122221222122221',
    '122221222122221',
    '012222111222210',
    '001222222222100',
    '000112222211000',
    '000001111100000',
  ],
  won: [
    '000001111100000',
    '000112222211000',
    '001222222222100',
    '012222222222210',
    '121111222111121',
    '121111222111121',
    '122222222222221',
    '122222222222221',
    '122222222222221',
    '122212222212221',
    '122221222122221',
    '012222111222210',
    '001222222222100',
    '000112222211000',
    '000001111100000',
  ],
  lost: [
    '000001111100000',
    '000112222211000',
    '001222222222100',
    '012222222222210',
    '122121222121221',
    '122212222212221',
    '122121222121221',
    '122222222222221',
    '122222111222221',
    '122221222122221',
    '122212222212221',
    '012211222112210',
    '001222222222100',
    '000112222211000',
    '000001111100000',
  ],
};

function MinesweeperNumber({ value }: { value: number }) {
  return (
    <span className={`minesweeper-number minesweeper-number--${value}`} aria-hidden="true">
      {CELL_NUMBER_BITMAPS[value].flatMap((row, rowIndex) =>
        [...row].map((pixel, columnIndex) => (
          <i
            className={
              pixel === '1'
                ? 'minesweeper-number-pixel minesweeper-number-pixel--on'
                : 'minesweeper-number-pixel'
            }
            key={`${rowIndex}-${columnIndex}`}
          />
        )),
      )}
    </span>
  );
}

function MinesweeperStartMarker() {
  return (
    <svg
      className="minesweeper-start-marker"
      viewBox="0 0 100 100"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
    >
      <path d="M18 14 Q14 14 17 19 L39 49 L17 80 Q14 85 20 85 H38 Q42 85 45 80 L50 71 L56 80 Q59 85 64 85 H82 Q88 85 84 80 L61 50 L84 19 Q88 14 82 14 H64 Q60 14 56 19 L50 28 L44 19 Q41 14 37 14 Z" />
    </svg>
  );
}

function MinesweeperFlag() {
  return (
    <span className="minesweeper-flag" aria-hidden="true">
      {FLAG_BITMAP.flatMap((row, rowIndex) =>
        [...row].map((pixel, columnIndex) => (
          <i
            className={`minesweeper-flag-pixel minesweeper-flag-pixel--${pixel}`}
            key={`${rowIndex}-${columnIndex}`}
          />
        )),
      )}
    </span>
  );
}

function MinesweeperFace({ state }: { state: MinesweeperFaceState }) {
  return (
    <span
      className={`minesweeper-face minesweeper-face--${state}`}
      data-face={state}
      aria-hidden="true"
    >
      {FACE_BITMAPS[state].flatMap((row, rowIndex) =>
        [...row].map((pixel, columnIndex) => (
          <i
            className={`minesweeper-face-pixel minesweeper-face-pixel--${pixel}`}
            key={`${rowIndex}-${columnIndex}`}
          />
        )),
      )}
    </span>
  );
}

function MinesweeperDisplay({ value, label }: { value: number; label: string }) {
  const counter = formatCounter(value);
  return (
    <output className="minesweeper-display" aria-label={label}>
      <span className="sr-only">{counter}</span>
      <span className="minesweeper-digits" aria-hidden="true">
        {[...counter].map((digit, index) => (
          <span className="minesweeper-digit" data-digit={digit} key={`${index}-${digit}`}>
            {DISPLAY_SEGMENTS.map((segment) => (
              <i className={`minesweeper-segment minesweeper-segment--${segment}`} key={segment} />
            ))}
          </span>
        ))}
      </span>
    </output>
  );
}

export function MinesweeperGame({
  puzzle,
  savedState,
  disabled,
  reducedMotion,
  onStateChange,
  onComplete,
}: GameComponentProps) {
  if (!isMinesweeperPuzzle(puzzle.payload)) {
    throw new Error('Minesweeper received an invalid puzzle payload.');
  }
  const minesweeper = puzzle.payload as MinesweeperPuzzle;
  const initial = useMemo(() => readSavedState(savedState, minesweeper), [savedState, minesweeper]);
  const [marks, setMarks] = useState(initial.marks);
  const [selectedIndex, setSelectedIndex] = useState(initial.selectedIndex);
  const [mode, setMode] = useState<MinesweeperMode>(initial.mode);
  const [gameStatus, setGameStatus] = useState<MinesweeperStatus>('playing');
  const [hitIndex, setHitIndex] = useState<number | null>(null);
  const [status, setStatus] = useState('Find the green X to begin');
  const [timerStartedAt, setTimerStartedAt] = useState(initial.timerStartedAt);
  const [elapsedMs, setElapsedMs] = useState(initial.elapsedMs);
  const [now, setNow] = useState(Date.now());
  const [pressing, setPressing] = useState(false);
  const marksRef = useRef(marks);
  const selectedRef = useRef(selectedIndex);
  const modeRef = useRef(mode);
  const timerStartedRef = useRef(timerStartedAt);
  const elapsedRef = useRef(elapsedMs);
  const gameStatusRef = useRef<MinesweeperStatus>('playing');
  const gridRef = useRef<HTMLDivElement>(null);
  const longPressRef = useRef<number | null>(null);
  const suppressClickRef = useRef<number | null>(null);
  const completionTimeoutRef = useRef<number | null>(null);

  const currentElapsed = timerStartedAt ? elapsedMs + Math.max(0, now - timerStartedAt) : elapsedMs;

  useEffect(() => {
    if (!timerStartedAt || gameStatus !== 'playing') return;
    const interval = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(interval);
  }, [gameStatus, timerStartedAt]);

  useEffect(
    () => () => {
      if (longPressRef.current !== null) window.clearTimeout(longPressRef.current);
      if (completionTimeoutRef.current !== null) window.clearTimeout(completionTimeoutRef.current);
    },
    [],
  );

  const persist = useCallback(
    (nextMarks = marksRef.current) => {
      onStateChange({
        version: 1,
        marks: nextMarks,
        selectedIndex: selectedRef.current,
        mode: modeRef.current,
        timerStartedAt: timerStartedRef.current,
        elapsedMs: elapsedRef.current,
      });
    },
    [onStateChange],
  );

  const startTimer = useCallback(() => {
    if (timerStartedRef.current !== null) return;
    const started = Date.now();
    timerStartedRef.current = started;
    setTimerStartedAt(started);
    setNow(started);
  }, []);

  const stopTimer = useCallback(() => {
    if (timerStartedRef.current !== null) {
      elapsedRef.current += Math.max(0, Date.now() - timerStartedRef.current);
      setElapsedMs(elapsedRef.current);
      timerStartedRef.current = null;
      setTimerStartedAt(null);
    }
  }, []);

  const focusCell = (index: number) => {
    window.requestAnimationFrame(() => {
      gridRef.current?.querySelector<HTMLElement>(`[data-grid-index="${index}"]`)?.focus();
    });
  };

  const select = useCallback(
    (index: number, focus = false) => {
      selectedRef.current = index;
      setSelectedIndex(index);
      persist();
      if (focus) focusCell(index);
    },
    [persist],
  );

  const finishWin = useCallback(
    (nextMarks: MinesweeperMark[]) => {
      const completed = [...nextMarks] as MinesweeperMark[];
      for (const mine of minesweeper.mines) completed[mine] = 2;
      marksRef.current = completed;
      gameStatusRef.current = 'won';
      setMarks(completed);
      setGameStatus('won');
      setStatus('Minefield cleared!');
      stopTimer();
      persist(completed);
      completionTimeoutRef.current = window.setTimeout(
        () =>
          onComplete({
            kind: 'solved',
            baseDeltaMs: -15_000,
            message: 'Minefield cleared!',
            metadata: { elapsedMs: elapsedRef.current, autoFlagged: true },
          }),
        reducedMotion ? 180 : 1_000,
      );
    },
    [minesweeper.mines, onComplete, persist, reducedMotion, stopTimer],
  );

  const finishLoss = useCallback(
    (nextMarks: MinesweeperMark[], mineIndex: number) => {
      const flags = correctFlagCount(nextMarks, minesweeper);
      marksRef.current = nextMarks;
      gameStatusRef.current = 'lost';
      setMarks(nextMarks);
      setHitIndex(mineIndex);
      setGameStatus('lost');
      setStatus(`Mine hit · ${flags} correct ${flags === 1 ? 'flag' : 'flags'}`);
      stopTimer();
      persist(nextMarks);
      completionTimeoutRef.current = window.setTimeout(
        () =>
          onComplete({
            kind: 'failed',
            baseDeltaMs: minesweeperFailureDelta(flags),
            message: 'Boom!',
            metadata: { correctFlags: flags, hitIndex: mineIndex, elapsedMs: elapsedRef.current },
          }),
        reducedMotion ? 150 : 600,
      );
    },
    [minesweeper, onComplete, persist, reducedMotion, stopTimer],
  );

  const commitSafeMarks = useCallback(
    (nextMarks: MinesweeperMark[]) => {
      marksRef.current = nextMarks;
      setMarks(nextMarks);
      persist(nextMarks);
      if (isMinesweeperWon(nextMarks, minesweeper)) finishWin(nextMarks);
    },
    [finishWin, minesweeper, persist],
  );

  const chord = useCallback(
    (index: number) => {
      if (disabled || gameStatusRef.current !== 'playing') return;
      startTimer();
      const result = chordInto(marksRef.current, minesweeper, index);
      if (!result.changed) {
        setStatus('Match the adjacent flag count to chord');
        return;
      }
      const mine = result.hitMine
        ? result.marks.findIndex(
            (mark, cellIndex) => mark === 1 && minesweeper.values[cellIndex] === -1,
          )
        : -1;
      if (mine >= 0) finishLoss(result.marks, mine);
      else commitSafeMarks(result.marks);
    },
    [commitSafeMarks, disabled, finishLoss, minesweeper, startTimer],
  );

  const reveal = useCallback(
    (index: number) => {
      if (disabled || gameStatusRef.current !== 'playing') return;
      if (marksRef.current[index] === 2) return;
      if (marksRef.current[index] === 1) {
        chord(index);
        return;
      }
      startTimer();
      const result = revealInto(marksRef.current, minesweeper, index);
      if (result.hitMine) finishLoss(result.marks, index);
      else {
        setStatus(
          index === minesweeper.startIndex
            ? `${minesweeper.openingSize} safe cells opened`
            : 'Safe',
        );
        commitSafeMarks(result.marks);
      }
    },
    [chord, commitSafeMarks, disabled, finishLoss, minesweeper, startTimer],
  );

  const toggleFlag = useCallback(
    (index: number) => {
      if (disabled || gameStatusRef.current !== 'playing' || marksRef.current[index] === 1) return;
      startTimer();
      const next = [...marksRef.current] as MinesweeperMark[];
      next[index] = next[index] === 2 ? 0 : 2;
      marksRef.current = next;
      selectedRef.current = index;
      setMarks(next);
      setSelectedIndex(index);
      setStatus(next[index] === 2 ? 'Flag placed' : 'Flag removed');
      persist(next);
    },
    [disabled, persist, startTimer],
  );

  const reset = useCallback(() => {
    if (disabled) return;
    if (completionTimeoutRef.current !== null) window.clearTimeout(completionTimeoutRef.current);
    const next = Array.from({ length: MINESWEEPER_CELL_COUNT }, () => 0) as MinesweeperMark[];
    marksRef.current = next;
    selectedRef.current = minesweeper.startIndex;
    timerStartedRef.current = null;
    elapsedRef.current = 0;
    gameStatusRef.current = 'playing';
    setMarks(next);
    setSelectedIndex(minesweeper.startIndex);
    setTimerStartedAt(null);
    setElapsedMs(0);
    setGameStatus('playing');
    setHitIndex(null);
    setStatus('Board reset · find the green X');
    setPressing(false);
    persist(next);
  }, [disabled, minesweeper.startIndex, persist]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (disabled || gameStatusRef.current !== 'playing') return;
      if (isGridArrowKey(event.key)) {
        event.preventDefault();
        const next = moveGridSelection(
          selectedRef.current,
          event.key,
          minesweeper.height,
          minesweeper.width,
        );
        select(next, true);
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        reveal(selectedRef.current);
      } else if (event.key.toLowerCase() === 'f') {
        event.preventDefault();
        toggleFlag(selectedRef.current);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [disabled, minesweeper.height, minesweeper.width, reveal, select, toggleFlag]);

  const clearLongPress = () => {
    if (longPressRef.current !== null) window.clearTimeout(longPressRef.current);
    longPressRef.current = null;
    setPressing(false);
  };

  const pointerDown = (event: ReactPointerEvent<HTMLButtonElement>, index: number) => {
    if (disabled || gameStatusRef.current !== 'playing') return;
    select(index);
    setPressing(true);
    if (event.pointerType === 'touch' && event.button === 0) {
      longPressRef.current = window.setTimeout(() => {
        suppressClickRef.current = index;
        toggleFlag(index);
        setPressing(false);
      }, 350);
    }
  };

  const cellClick = (index: number) => {
    if (suppressClickRef.current === index) {
      suppressClickRef.current = null;
      return;
    }
    if (window.innerWidth <= 650 && modeRef.current === 'flag') toggleFlag(index);
    else reveal(index);
  };

  const middleClick = (event: ReactMouseEvent<HTMLButtonElement>, index: number) => {
    if (event.button !== 1) return;
    event.preventDefault();
    chord(index);
  };

  const setInputMode = (nextMode: MinesweeperMode) => {
    if (disabled || gameStatusRef.current !== 'playing') return;
    modeRef.current = nextMode;
    setMode(nextMode);
    setStatus(`${nextMode === 'reveal' ? 'Reveal' : 'Flag'} mode`);
    persist();
  };

  const flagsPlaced = marks.filter((mark) => mark === 2).length;
  const faceState: MinesweeperFaceState =
    gameStatus === 'won' ? 'won' : gameStatus === 'lost' ? 'lost' : pressing ? 'pressed' : 'ready';

  return (
    <section className="minesweeper-game" aria-labelledby="minesweeper-title">
      <h1 id="minesweeper-title" className="sr-only">
        Minesweeper
      </h1>
      <div className="minesweeper-frame">
        <header className="minesweeper-panel">
          <MinesweeperDisplay
            value={minesweeper.mineCount - flagsPlaced}
            label={`${minesweeper.mineCount - flagsPlaced} mines remaining`}
          />
          <button
            type="button"
            className="minesweeper-smiley"
            aria-label="Reset Minesweeper"
            disabled={disabled}
            onClick={reset}
          >
            <MinesweeperFace state={faceState} />
          </button>
          <MinesweeperDisplay
            value={Math.floor(currentElapsed / 1_000)}
            label={`${Math.floor(currentElapsed / 1_000)} seconds elapsed`}
          />
        </header>

        <div
          ref={gridRef}
          className="minesweeper-grid"
          role="grid"
          aria-label="12 by 12 Minesweeper grid"
          onPointerLeave={clearLongPress}
        >
          {marks.map((mark, index) => {
            const row = Math.floor(index / minesweeper.width) + 1;
            const column = (index % minesweeper.width) + 1;
            const value = minesweeper.values[index];
            const mineVisible = gameStatus === 'lost' && value === -1;
            const wrongFlag = gameStatus === 'lost' && mark === 2 && value !== -1;
            const hit = hitIndex === index;
            const shownValue = mark === 1 && value > 0 ? value : '';
            const description = hit
              ? 'hit mine'
              : wrongFlag
                ? 'incorrect flag'
                : mineVisible
                  ? mark === 2
                    ? 'correctly flagged mine'
                    : 'mine'
                  : mark === 2
                    ? 'flagged'
                    : mark === 1
                      ? value === 0
                        ? 'revealed empty'
                        : `revealed ${value}`
                      : index === minesweeper.startIndex
                        ? 'covered, guaranteed safe start'
                        : 'covered';
            return (
              <button
                key={index}
                type="button"
                role="gridcell"
                data-grid-index={index}
                aria-label={`Row ${row}, column ${column}, ${description}`}
                aria-selected={selectedIndex === index}
                disabled={disabled || gameStatus !== 'playing'}
                className={[
                  'minesweeper-cell',
                  mark === 1 ? 'minesweeper-cell--revealed' : '',
                  mark === 1 && value > 0 ? `minesweeper-cell--value-${value}` : '',
                  mark === 2 ? 'minesweeper-cell--flagged' : '',
                  index === minesweeper.startIndex && mark === 0 ? 'minesweeper-cell--start' : '',
                  mineVisible ? 'minesweeper-cell--mine' : '',
                  hit ? 'minesweeper-cell--hit' : '',
                  wrongFlag ? 'minesweeper-cell--wrong-flag' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => cellClick(index)}
                onContextMenu={(event) => {
                  event.preventDefault();
                  select(index);
                  toggleFlag(index);
                }}
                onMouseDown={(event) => middleClick(event, index)}
                onPointerDown={(event) => pointerDown(event, index)}
                onPointerUp={clearLongPress}
                onPointerCancel={clearLongPress}
              >
                {hit ? (
                  '💥'
                ) : wrongFlag ? (
                  '✕'
                ) : mineVisible ? (
                  mark === 2 ? (
                    <MinesweeperFlag />
                  ) : (
                    '●'
                  )
                ) : mark === 2 ? (
                  <MinesweeperFlag />
                ) : mark === 0 && index === minesweeper.startIndex ? (
                  <MinesweeperStartMarker />
                ) : shownValue ? (
                  <MinesweeperNumber value={value} />
                ) : (
                  ''
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div className="minesweeper-modes" role="group" aria-label="Minesweeper input mode">
        <button
          type="button"
          aria-pressed={mode === 'reveal'}
          disabled={disabled || gameStatus !== 'playing'}
          onClick={() => setInputMode('reveal')}
        >
          ◉ Reveal
        </button>
        <button
          type="button"
          aria-pressed={mode === 'flag'}
          disabled={disabled || gameStatus !== 'playing'}
          onClick={() => setInputMode('flag')}
        >
          ⚑ Flag
        </button>
      </div>
      <p className="minesweeper-help">
        Right-click or hold to flag · click a revealed number to chord
      </p>
      <p className="minesweeper-status" role="status" aria-live="assertive">
        {status}
      </p>
    </section>
  );
}
